import React, { useState, useEffect } from 'react';
import { usePIM } from '../context/PIMContext';
import { useToast } from '../context/ToastContext';
import UserModal from './UserModal';
import ThemeModal from './modals/ThemeModal';
import { ConfirmationModal } from './ui/ConfirmationModal';
import { Trash2, Edit, Plus, Shield, User, Eye, Palette, Download, Loader } from 'lucide-react';
import { BASE_UPLOAD_URL } from '../config';

import { useSortableData } from '../hooks/useSortableData';
import { SortableHeader } from './ui/SortableHeader';

const UserManagementTab = () => {
    const { users, fetchUsers, deleteUser, currentUser } = usePIM();
    const { toast } = useToast();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isBackingUp, setIsBackingUp] = useState(false);

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        isDestructive: false
    });

    // Sorting
    const { items: sortedUsers, requestSort, sortConfig } = useSortableData(users);

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleAddUser = () => {
        setSelectedUser(null);
        setIsModalOpen(true);
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const handleDeleteUserClick = (user) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete User',
            message: `Are you sure you want to delete user "${user.username}"? This action cannot be undone.`,
            isDestructive: true,
            onConfirm: async () => {
                const result = await deleteUser(user.id);
                if (result.success) {
                    toast.success(`User "${user.username}" deleted successfully.`);
                } else {
                    toast.error('Failed to delete user: ' + result.message);
                }
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    const handleCreateBackupClick = () => {
        setConfirmModal({
            isOpen: true,
            title: 'Create System Backup',
            message: 'Create a full system backup (Database + Files)? This might take a moment.',
            isDestructive: false,
            confirmText: 'Create Backup',
            onConfirm: () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                performBackup();
            }
        });
    };

    const performBackup = async () => {
        setIsBackingUp(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/backup', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const err = await response.text();
                throw new Error(err);
            }

            const data = await response.json();
            toast.success(`Backup created: ${data.filename}`, 5000);

            // Trigger download
            const link = document.createElement('a');
            link.href = `${BASE_UPLOAD_URL}${data.url}`;
            link.download = data.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error("Backup failed:", error);
            toast.error("Backup failed: " + error.message);
        } finally {
            setIsBackingUp(false);
        }
    };

    const getRoleIcon = (role) => {
        switch (role) {
            case 'Admin': return <Shield size={16} className="text-red-500" />;
            case 'Editor': return <Edit size={16} className="text-blue-500" />;
            case 'Viewer': return <Eye size={16} className="text-green-500" />;
            default: return <User size={16} className="text-gray-500" />;
        }
    };

    return (
        <div className="h-full flex flex-col bg-[var(--color-bg-content)] text-[var(--color-text-primary)] p-4 overflow-hidden">
            <ConfirmationModal
                isOpen={confirmModal.isOpen}
                onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                isDestructive={confirmModal.isDestructive}
                confirmText={confirmModal.confirmText || 'Confirm'}
            />

            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-stone-100">User Management</h2>
                <div className="flex gap-3">
                    <button
                        onClick={handleCreateBackupClick}
                        disabled={isBackingUp}
                        className="flex items-center gap-2 px-4 py-2 bg-stone-800 hover:bg-stone-700 text-stone-200 rounded-md transition-colors border border-stone-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isBackingUp ? <Loader size={18} className="animate-spin" /> : <Download size={18} />}
                        {isBackingUp ? 'Backing up...' : 'Full Backup'}
                    </button>
                    <button
                        onClick={() => setIsThemeModalOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-stone-800 hover:bg-stone-700 text-stone-200 rounded-md transition-colors border border-stone-600"
                    >
                        <Palette size={18} />
                        Theme Settings
                    </button>
                    <button
                        onClick={handleAddUser}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                    >
                        <Plus size={18} />
                        Add User
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-auto bg-stone-800 rounded-lg border border-stone-700 shadow-lg">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-stone-950 sticky top-0 z-10">
                        <tr>
                            <SortableHeader label="Username" sortKey="username" currentSort={sortConfig} onSort={requestSort} />
                            <SortableHeader label="Email" sortKey="email" currentSort={sortConfig} onSort={requestSort} />
                            <SortableHeader label="Role" sortKey="role" currentSort={sortConfig} onSort={requestSort} />
                            <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={requestSort} />
                            <th className="p-4 font-semibold text-stone-400 border-b border-stone-700 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-700">
                        {sortedUsers.map(user => (
                            <tr key={user.id} className="hover:bg-stone-700/50 transition-colors">
                                <td className="p-4 flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-stone-600 flex items-center justify-center text-sm font-bold">
                                        {user.username.charAt(0).toUpperCase()}
                                    </div>
                                    <span className="font-medium">{user.username}</span>
                                </td>
                                <td className="p-4 text-stone-400">{user.email}</td>
                                <td className="p-4">
                                    <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-stone-900/50 w-fit border border-stone-700 text-sm">
                                        {getRoleIcon(user.role)}
                                        <span>{user.role}</span>
                                    </div>
                                </td>
                                <td className="p-4">
                                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${user.status === 'Active' ? 'bg-green-900/30 text-green-400 border border-green-800' : 'bg-red-900/30 text-red-400 border border-red-800'
                                        }`}>
                                        {user.status}
                                    </span>
                                </td>
                                <td className="p-4 text-right">
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={() => handleEditUser(user)}
                                            className="p-2 text-stone-400 hover:text-blue-400 hover:bg-stone-600 rounded-md transition-colors"
                                            title="Edit User"
                                        >
                                            <Edit size={18} />
                                        </button>
                                        {user.id !== currentUser.id && ( // Prevent deleting yourself
                                            <button
                                                onClick={() => handleDeleteUserClick(user)}
                                                className="p-2 text-stone-400 hover:text-red-400 hover:bg-stone-600 rounded-md transition-colors"
                                                title="Delete User"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {sortedUsers.length === 0 && (
                            <tr>
                                <td colSpan="5" className="p-8 text-center text-stone-500">
                                    No users found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <UserModal
                    user={selectedUser}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
            <ThemeModal
                isOpen={isThemeModalOpen}
                onClose={() => setIsThemeModalOpen(false)}
            />
        </div>
    );
};

export default UserManagementTab;
